import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Tabs } from '@components';
import { FEATURE_FLAGS } from '@constants/feature';
import { RoleType } from '@features/team-management/constants';
import Head from '@layouts/head/Head';
import { TabPill } from 'types/tabs';

import { CalendarIntegrationCard } from '../components/lead-settings';
import LeadSquaredConfiguration from '../components/lead-settings/LeadSquaredConfiguration';
import MetaIntegration from '../components/lead-settings/MetaIntegration';
import { LeadSettingIds, leadSettingsTabs } from '../constants/leadSettings';
import useLeadAgentRestrictions from '../hooks/useLeadAgentRestrictions';

const LeadSettings = () => {
  const { orgFeatureFlags, rolesAndPermissions } = useSelector(
    (state: any) => state.user
  );
  const {
    isAssigneeSelectionDisabled,
    isLoading: isLoadingAssigneeRestriction
  } = useLeadAgentRestrictions();
  const isSalesAgent = rolesAndPermissions?.role === RoleType.SALES_AGENT;

  const isMetaLeadIngestionEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.META_LEAD_INGESTION
  );

  // Meta sync is bulk lead ingestion; hide it while org blocks sales agents from
  // creating leads (same rule as CSV / manual add on the list page).
  const showMetaIntegrationTab =
    isMetaLeadIngestionEnabled &&
    !isLoadingAssigneeRestriction &&
    !isAssigneeSelectionDisabled;

  const filteredTabs = useMemo(() => {
    const tabVisibility: Record<string, boolean> = {
      [LeadSettingIds.LEAD_CONFIGURATION]: !isSalesAgent,
      [LeadSettingIds.META_INTEGRATION]: showMetaIntegrationTab
    };
    return leadSettingsTabs.filter((tab) => tabVisibility[tab.id] ?? true);
  }, [isSalesAgent, showMetaIntegrationTab]);

  const [selectedTab, setSelectedTab] = useState<TabPill>(filteredTabs[0]);

  const handleTabChange = (tab: TabPill) => {
    setSelectedTab(tab);
  };

  return (
    <div className="page bg-bgBase">
      <Head title="Settings" />
      <h1 className="text-2xl font-bold text-content">Settings</h1>
      <Tabs
        tabs={filteredTabs}
        selectedTab={selectedTab}
        onTabSelect={handleTabChange}
      />
      <div className="mt-6">
        {selectedTab.id === LeadSettingIds.CALENDAR_CONFERENCING && (
          <CalendarIntegrationCard />
        )}
        {selectedTab.id === LeadSettingIds.LEAD_CONFIGURATION && (
          <LeadSquaredConfiguration />
        )}
        {selectedTab.id === LeadSettingIds.META_INTEGRATION &&
          showMetaIntegrationTab && <MetaIntegration />}
      </div>
    </div>
  );
};

export default LeadSettings;
